import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CK_CONFIGS } from "configs";
import React, { useState, useEffect } from "react";
import { Row, Col, Form, button, Modal } from "react-bootstrap";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import validate from 'validate.js';
import {
  addObjects,
  hideObjectsValidationError,
  objectTypesListFetch,
  objectsParentListFetch,
} from "actions";


const schema = {
  name: {
    presence: { allowEmpty: false, message: '^Workspace name is required' },
  },
  type_id: {
    presence: { allowEmpty: false, message: '^ Please select object type' },
  },
  object_code: {
    presence: { allowEmpty: false, message: ' is required' },
  }
}

const ObjectAdd = ({ show, onHide }) => {
  const dispatch = useDispatch();

  const objectsState = useSelector(state => state.objectsState);
  const objectTypesState = useSelector(state => state.objectTypesState);
  const session = useSelector(state => state.session);

  const [ObjectTypeValue, setObjectTypeValue] = useState(null);
  const [ParentValue, setParentValue] = useState(null);
  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      'object_viewed_id': '',
      parent_id: '',
      show_in_menu: false,
    },
    touched: {
      'object_viewed_id': true,
    },
    errors: {}
  });

  useEffect(() => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        'show_in_menu': false
      },
      touched: {
        ...formState.touched,
        'show_in_menu': true
      }
    }));
  }, []);

  useEffect(() => {
    dispatch(objectTypesListFetch(session.current_page_permissions.object_id))
  }, [dispatch, session.current_page_permissions.object_id]);

  const formattedObjectsType = objectTypesState.objectTypesList.map(type => ({
    label: type.opt_display,
    value: type.id
  }));

  const formattedParentObjects = Array.isArray(objectsState.objectsParentsList)
    ? objectsState.objectsParentsList.map(type => ({
      label: type.name,
      value: type.id
    }))
    : [];

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors || {}
    }));
  }, [formState.values]);

  useEffect(() => {
    if (!isEmpty(objectsState.validation_error)) {
      const errors = objectsState.validation_error;
      setFormState(formState => ({
        ...formState,
        isValid: errors ? false : true,
        errors: errors || {}
      }));
    }
  }, [objectsState.validation_error]);

  const setDescription = description => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        'description': description
      },
      touched: {
        ...formState.touched,
        'description': true
      }
    }));
    dispatch(hideObjectsValidationError('description'))
  }

  const objectTypesChange = (type_id, type_name) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        'type_id': type_id
      },
      touched: {
        ...formState.touched,
        'type_id': true
      }
    }));
    dispatch(objectsParentListFetch(type_id, type_name))
    dispatch(hideObjectsValidationError('type_id'))
  }

  const setParentId = (parent_id) => {
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        'parent_id': parent_id,
      },
      touched: {
        ...formState.touched,
        'parent_id': true,
      },
    }));
    dispatch(hideObjectsValidationError('parent_id'));
  };

  const handleChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
    dispatch(hideObjectsValidationError(event.target.name))
  }

  // Remove themeState from customStyles
  const customStyles = () => ({
    control: (provided, state) => ({
      ...provided,
      borderRadius: "60px",
      border: "1px solid #ccc",
      borderColor: state.isFocused ? "#888" : "#ddd",
      backgroundColor: "#fff",
      color: "#000",
      boxShadow: state.isFocused ? provided.boxShadow : "none",
      "&:hover": {
        backgroundColor: "#f0f0f0",
        borderColor: state.isFocused ? "#888" : "#ccc",
      },
    }),
  });

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  const [checked, setChecked] = useState(false);

  const handleToggle = () => {
    const newChecked = !formState.values.show_in_menu;
    setChecked(!checked);
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        show_in_menu: newChecked
      },
      touched: {
        ...formState.touched,
        show_in_menu: true
      }
    }));
    dispatch(hideObjectsValidationError('show_in_menu'));
  };

  const handleCancel = () => {
    if (onHide) onHide();
  };

  const handleSubmit = async event => {
    event.preventDefault();
    dispatch(addObjects(formState.values));
    if (onHide) onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Add Object</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <div className="access-content">
            <Row>
              <Col lg={4}>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label className="font-13 font-weight-400 page-heading">Name</Form.Label>
                  <Form.Control
                    type="name"
                    name="name"
                    onChange={handleChange}
                    value={formState.values.name || ''}
                    isInvalid={hasError('name')}
                    placeholder="Enter name"
                    className='input-field'
                  />
                  {hasError('name') && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.name[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={4}>
                {objectTypesState.objectTypesList && (
                  <>
                    <Form.Label className="font-13 font-weight-normal page-heading">
                      Select Type
                    </Form.Label>
                    <Select
                      id="type_id"
                      placeholder="Select"
                      isSearchable={true}
                      value={ObjectTypeValue}
                      onChange={(newValue) => {
                        if (newValue) {
                          setObjectTypeValue(newValue);
                          objectTypesChange(newValue.value, newValue.label);
                        } else {
                          setObjectTypeValue(null);
                          objectTypesChange('', '');
                        }
                      }}
                      options={formattedObjectsType}
                      className="font-14 font-weight-normal worspace-access"
                      styles={customStyles()}
                    />
                    {hasError('type_id') && (
                      <Form.Control.Feedback type="invalid">
                        {formState.errors.type_id[0]}
                      </Form.Control.Feedback>
                    )}
                  </>
                )}
              </Col>
              <Col lg={4}>
                {objectsState.objectsParentsList && (
                  <>
                    <Form.Label className="font-13 font-weight-normal page-heading">
                      Select Parent Object
                    </Form.Label>
                    <Select
                      placeholder="Select"
                      isSearchable={true}
                      value={ParentValue}
                      onChange={(newValue) => {
                        if (newValue) {
                          setParentValue(newValue);
                          setParentId(newValue.value);
                        } else {
                          setParentValue(null);
                          setParentId('');
                        }
                      }}
                      options={formattedParentObjects}
                      className="font-14 font-weight-normal worspace-access"
                      styles={customStyles()}
                    />
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col lg={4}>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label className="font-13 font-weight-400 page-heading">
                    Object Url
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Object Url"
                    name="object_url"
                    onChange={handleChange}
                    value={formState.values.object_url || ""}
                    className='input-field'
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label className="font-13 font-weight-400 page-heading">
                    Unique Object Code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="object_code"
                    placeholder="Select"
                    onChange={handleChange}
                    value={formState.values.object_code || ""}
                    isInvalid={hasError("object_code")}
                    className='input-field'
                  />
                  {hasError('object_code') && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.object_code[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label className="font-13 font-weight-400 page-heading">
                    Menu Order
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="menu_order"
                    placeholder="Select"
                    value={formState.values.menu_order || ''}
                    onChange={handleChange}
                    className='input-field'
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label className="font-13 font-weight-400 page-heading">
                    Menu Icon{" "}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Select"
                    name="menu_icon"
                    onChange={handleChange}
                    value={formState.values.menu_icon || ""}
                    className='input-field'
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <Form.Check
                  type="switch"
                  name="show_in_menu"
                  id="custom-switch"
                  label="Show In Menu"
                  checked={checked}
                  onChange={handleToggle}
                  className="custom-switch page-heading font-14"
                />
              </Col>
            </Row>
            <Row className="my-4">
              <Col md={12} className="mx-auto mb-3=4">
                <CKEditor
                  editor={ClassicEditor}
                  config={CK_CONFIGS(localStorage.getItem("token"))}
                  data={formState.values.description || ""}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setDescription(data);
                  }}
                />
                {hasError("description") && (
                  <Form.Text className="text-danger">
                    {formState.errors.description[0]}
                  </Form.Text>
                )}
              </Col>
            </Row>
            <div className="d-flex justify-content-start align-items-center gap-3">
              <button
                variant="none"
                className="n-u-btn w-100"
                disabled={!formState.isValid}
                type="submit"
              >
                Create
              </button>
              <button
                variant="none"
                className="c-b-btn w-100"
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ObjectAdd;